/**
 * Centralized utility for job category map pin icons
 * These are used for category-based map markers in the Jobs marketplace
 * 
 * Pattern follows serviceTypeIcons.ts and partsTypeIcons.ts structure
 */

import helicopterSilhouetteIcon from '@/assets/helicopter-silhouette-icon.png';

// Lucide icon SVG data URLs for map markers (black strokes on transparent background)
// These are inline SVGs converted to data URLs for canvas rendering

const createLucideSvgDataUrl = (svgPath: string, viewBox = '0 0 24 24'): string => {
  // Generate at 256x256 for high-quality downscaling to ~52px display size
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="256" height="256" fill="none" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${svgPath}</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Lucide icon paths for each job category
const LUCIDE_PATHS = {
  // Wrench - mechanic
  wrench: '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>',
  // ClipboardCheck - camo
  clipboardCheck: '<rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><path d="m9 14 2 2 4-4"/>',
  // Settings - part21
  settings: '<path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/>',
  // Users - cabin_crew
  users: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  // Truck - ground_ops
  truck: '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18h2"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>',
  // Radio - atc_dispatch
  radio: '<path d="M4.9 19.1C1 15.2 1 8.8 4.9 4.9"/><path d="M7.8 16.2c-2.3-2.3-2.3-6.1 0-8.5"/><circle cx="12" cy="12" r="2"/><path d="M16.2 7.8c2.3 2.3 2.3 6.1 0 8.5"/><path d="M19.1 4.9C23 8.8 23 15.1 19.1 19"/>',
  // GraduationCap - training
  graduationCap: '<path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"/><path d="M22 10v6"/><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"/>',
  // Radar - drones_uas (satellite/signal icon that represents UAS operations)
  radar: '<path d="M19.07 4.93A10 10 0 0 0 6.99 3.34"/><path d="M4 6h.01"/><path d="M2.29 9.62A10 10 0 1 0 21.31 8.35"/><path d="M16.24 7.76A6 6 0 1 0 8.23 16.67"/><path d="M12 18h.01"/><circle cx="12" cy="12" r="2"/><path d="m13.41 10.59 5.66-5.66"/>',
  // Building - airport_ops
  building: '<rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/>',
  // Cog - engineering
  cog: '<path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z"/><path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/><path d="M12 2v2"/><path d="M12 22v-2"/><path d="m17 20.66-1-1.73"/><path d="M11 10.27 7 3.34"/><path d="m20.66 17-1.73-1"/><path d="m3.34 7 1.73 1"/><path d="M14 12h8"/><path d="M2 12h2"/><path d="m20.66 7-1.73 1"/><path d="m3.34 17 1.73-1"/><path d="m17 3.34-1 1.73"/><path d="m11 13.73-4 6.93"/>',
  // Briefcase - management
  briefcase: '<path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/>',
};

// Map of job categories to their marker icon data URLs
export type JobCategory = 'pilot' | 'mechanic' | 'camo' | 'part21' | 'cabin_crew' | 'ground_ops' | 'atc_dispatch' | 'training' | 'drones_uas' | 'airport_ops' | 'engineering' | 'management';

export const JOB_CATEGORY_MARKER_ICONS: Record<JobCategory, string> = {
  pilot: helicopterSilhouetteIcon, // Uses helicopter PNG
  mechanic: createLucideSvgDataUrl(LUCIDE_PATHS.wrench),
  camo: createLucideSvgDataUrl(LUCIDE_PATHS.clipboardCheck),
  part21: createLucideSvgDataUrl(LUCIDE_PATHS.settings),
  cabin_crew: createLucideSvgDataUrl(LUCIDE_PATHS.users),
  ground_ops: createLucideSvgDataUrl(LUCIDE_PATHS.truck),
  atc_dispatch: createLucideSvgDataUrl(LUCIDE_PATHS.radio),
  training: createLucideSvgDataUrl(LUCIDE_PATHS.graduationCap),
  drones_uas: createLucideSvgDataUrl(LUCIDE_PATHS.radar),
  airport_ops: createLucideSvgDataUrl(LUCIDE_PATHS.building),
  engineering: createLucideSvgDataUrl(LUCIDE_PATHS.cog),
  management: createLucideSvgDataUrl(LUCIDE_PATHS.briefcase),
};

/**
 * Get the marker icon for a specific job category
 * Use this for Google Maps markers
 */
export const getJobCategoryMarkerIcon = (category?: string): string => {
  if (!category) return JOB_CATEGORY_MARKER_ICONS.management; // Default to briefcase
  return JOB_CATEGORY_MARKER_ICONS[category as JobCategory] || JOB_CATEGORY_MARKER_ICONS.management;
};

/**
 * Configuration for job category display including labels and colors
 */
export const JOB_CATEGORY_CONFIG: Record<JobCategory, {
  label: string;
  shortLabel: string;
  color: string;
  bgColor: string;
}> = {
  pilot: {
    label: 'Pilots',
    shortLabel: 'Pilot',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  mechanic: {
    label: 'Aircraft Mechanics',
    shortLabel: 'Mechanic',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  camo: {
    label: 'CAMO',
    shortLabel: 'CAMO',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  part21: {
    label: 'Part-21',
    shortLabel: 'Part-21',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  cabin_crew: {
    label: 'Cabin Crew',
    shortLabel: 'Crew',
    color: 'text-pink-600',
    bgColor: 'bg-pink-100',
  },
  ground_ops: {
    label: 'Ground Operations',
    shortLabel: 'Ground',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  atc_dispatch: {
    label: 'ATC / Dispatch',
    shortLabel: 'ATC',
    color: 'text-red-600',
    bgColor: 'bg-red-100',
  },
  training: {
    label: 'Training',
    shortLabel: 'Training',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  drones_uas: {
    label: 'Drones & UAS',
    shortLabel: 'UAS',
    color: 'text-cyan-600',
    bgColor: 'bg-cyan-100',
  },
  airport_ops: {
    label: 'Airport Operations',
    shortLabel: 'Airport',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
  },
  engineering: {
    label: 'Engineering',
    shortLabel: 'Eng',
    color: 'text-teal-600',
    bgColor: 'bg-teal-100',
  },
  management: {
    label: 'Management',
    shortLabel: 'Mgmt',
    color: 'text-violet-600',
    bgColor: 'bg-violet-100',
  },
};

/**
 * Get category label from category value
 */
export const getJobCategoryLabel = (category?: string): string => {
  if (!category) return 'Job';
  return JOB_CATEGORY_CONFIG[category as JobCategory]?.label || category;
};

/**
 * Get category short label from category value
 */
export const getJobCategoryShortLabel = (category?: string): string => {
  if (!category) return 'Job';
  return JOB_CATEGORY_CONFIG[category as JobCategory]?.shortLabel || category;
};
